import { FC, memo, ReactNode, useMemo } from 'react'
import { parseMarkdown, createRenderTokensToJsx } from 'helpers/markdownHelpers'
import { createImageTokenRenderer, createVideoTokenRenderer, TokenRendererErrorHandler } from 'helpers/insightMarkdownHelper'
import { ImageMarkdownNode } from 'marked-ast'
import { captureWarning } from 'helpers/errorHelpers'

type MarkdownProps = {
  text: string
  onImage?: (token: ImageMarkdownNode, key: string) => ReactNode
  onVideo?: (token: ImageMarkdownNode, key: string) => ReactNode
  onError?: TokenRendererErrorHandler
}

export const Markdown: FC<MarkdownProps> = memo(({
  text,
  onImage,
  onVideo,
  onError,
}) => {
  const markdownTokens = useMemo(() => parseMarkdown(text), [text])

  const handleError: TokenRendererErrorHandler = useMemo((): TokenRendererErrorHandler => {
    if (onError) {
      return onError
    }
    return (tokenType, token) => {
      captureWarning(`Markdown token error ${ tokenType }!`, { token })
    }
  }, [onError])

  const handleImage = useMemo(() => onImage || createImageTokenRenderer(handleError), [onImage, handleError])
  const handleVideo = useMemo(() => onVideo || createVideoTokenRenderer(handleError), [onVideo, handleError])

  const renderTokensToJsx = useMemo(() => createRenderTokensToJsx({
    image: handleImage,
    video: handleVideo,
  }), [handleImage, handleVideo])
  const content = useMemo(() => renderTokensToJsx(markdownTokens), [markdownTokens, renderTokensToJsx])

  return (
    <>
      { content }
    </>
  )
})
